// AtualizaSolicitacaoCaixa - index.js
import { useState, useEffect, useCallback } from "react";
import {
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  User,
  DollarSign,
  MessageSquare,
  Eye,
  Check,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import {
  solicitacaoReaberturaCaixaService,
  solicitacaoAberturaCaixaService,
} from "../../services/apiService";
import { useNotification } from "../../hooks/useNotification";
import PageContainer from "../../components/PageContainer";
import {
  cardClass,
  cardHeaderClass,
  cardHeaderTitleClass,
  tableBodyClass,
  tableClass,
  tableHeadClass,
  tableHeaderCellClass,
  tableHeaderCellRightClass,
  tableWrapperClass,
} from "../../components/ui";

const AtualizaSolicitacaoCaixa = () => {
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDatasModal, setShowDatasModal] = useState(false);
  const [showAcaoModal, setShowAcaoModal] = useState(false);
  const [selectedSolicitacao, setSelectedSolicitacao] = useState(null);
  const [motivoResposta, setMotivoResposta] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("PENDENTE");
  const [tipoSolicitacao, setTipoSolicitacao] = useState("REABERTURA");
  const { showError, showSuccess } = useNotification();

  // Estado da paginação
  const [paginacao, setPaginacao] = useState({
    paginaAtual: 1,
    itensPorPagina: 6,
  });

  const loadSolicitacoes = useCallback(async () => {
    setLoading(true);
    try {
      let data;
      if (tipoSolicitacao === "REABERTURA") {
        data =
          await solicitacaoReaberturaCaixaService.listarSolicitacaoReabertura(
            filtroStatus
          );
      } else {
        data = await solicitacaoAberturaCaixaService.listarSolicitacaoAbertura(
          filtroStatus
        );
      }
      setSolicitacoes(data);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  }, [filtroStatus, tipoSolicitacao]);

  useEffect(() => {
    loadSolicitacoes();
  }, [loadSolicitacoes]);

  // Reset página quando aplicar novos filtros
  useEffect(() => {
    setPaginacao((prev) => ({ ...prev, paginaAtual: 1 }));
  }, [filtroStatus, tipoSolicitacao]);

  const formatarData = (data) => {
    if (!data) return "-";

    try {
      if (typeof data === "string" && data.includes("/")) {
        const [day, month, year] = data.split("/");
        const dateObj = new Date(year, month - 1, day);
        return dateObj.toLocaleDateString("pt-BR");
      }

      const dateObj = new Date(data);
      if (isNaN(dateObj.getTime())) {
        return "-";
      }

      return dateObj.toLocaleDateString("pt-BR");
    } catch (error) {
      return "-";
    }
  };

  const abrirModalDatas = (solicitacao) => {
    setSelectedSolicitacao(solicitacao);
    setShowDatasModal(true);
  };

  const fecharModalDatas = () => {
    setShowDatasModal(false);
    setSelectedSolicitacao(null);
  };

  const abrirModalAcao = (solicitacao) => {
    setSelectedSolicitacao(solicitacao);
    setMotivoResposta("");
    setShowAcaoModal(true);
  };

  const fecharModalAcao = () => {
    setShowAcaoModal(false);
    setSelectedSolicitacao(null);
    setMotivoResposta("");
  };

  const responderSolicitacao = async (status) => {
    setLoading(true);

    try {
      const respostaData = {
        status: status,
        motivo: motivoResposta.trim() || undefined,
      };

      if (tipoSolicitacao === "REABERTURA") {
        await solicitacaoReaberturaCaixaService.atualizarSolicitacaoReabertura(
          selectedSolicitacao.id,
          respostaData
        );
      } else {
        await solicitacaoAberturaCaixaService.atualizarSolicitacaoAbertura(
          selectedSolicitacao.id,
          respostaData
        );
      }

      showSuccess(`Solicitação ${status.toLowerCase()} com sucesso!`);
      fecharModalAcao();
      loadSolicitacoes();
    } catch (err) {
      showError(err.message || "Erro ao responder solicitação");
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "APROVADO":
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case "NEGADO":
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return <Clock className="w-4 h-4 text-yellow-600" />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "APROVADO":
        return "Aprovado";
      case "NEGADO":
        return "Negado";
      default:
        return "Pendente";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "APROVADO":
        return "bg-green-100 text-green-800";
      case "NEGADO":
        return "bg-red-100 text-red-800";
      default:
        return "bg-yellow-100 text-yellow-800";
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value || 0);
  };

  const getCurrentStatus = (item) => {
    return item.status;
  };

  // Cálculos de paginação
  const totalRegistros = solicitacoes.length;
  const totalPaginas = Math.ceil(totalRegistros / paginacao.itensPorPagina);
  const indiceInicial = (paginacao.paginaAtual - 1) * paginacao.itensPorPagina;
  const indiceFinal = indiceInicial + paginacao.itensPorPagina;
  const solicitacoesPaginadas = solicitacoes.slice(indiceInicial, indiceFinal);

  // Funções de navegação
  const irParaPrimeiraPagina = () => {
    setPaginacao((prev) => ({ ...prev, paginaAtual: 1 }));
  };

  const irParaUltimaPagina = () => {
    setPaginacao((prev) => ({ ...prev, paginaAtual: totalPaginas }));
  };

  const irParaPaginaAnterior = () => {
    setPaginacao((prev) => ({
      ...prev,
      paginaAtual: Math.max(1, prev.paginaAtual - 1),
    }));
  };

  const irParaProximaPagina = () => {
    setPaginacao((prev) => ({
      ...prev,
      paginaAtual: Math.min(totalPaginas, prev.paginaAtual + 1),
    }));
  };

  const irParaPagina = (numeroPagina) => {
    setPaginacao((prev) => ({ ...prev, paginaAtual: numeroPagina }));
  };

  // Gerar números das páginas para exibição
  const gerarNumerosPaginas = () => {
    const paginas = [];
    const maxPaginasVisiveis = 5;
    let inicio = Math.max(
      1,
      paginacao.paginaAtual - Math.floor(maxPaginasVisiveis / 2)
    );
    let fim = Math.min(totalPaginas, inicio + maxPaginasVisiveis - 1);

    // Ajustar início se estivermos no final
    if (fim - inicio < maxPaginasVisiveis - 1) {
      inicio = Math.max(1, fim - maxPaginasVisiveis + 1);
    }

    for (let i = inicio; i <= fim; i++) {
      paginas.push(i);
    }

    return paginas;
  };

  return (
    <PageContainer className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Gerenciar Solicitações de Caixa
        </h1>
        <p className="text-gray-600">
          Visualize e responda às solicitações de abertura e reabertura de caixa
          dos usuários
        </p>
      </div>

      <div className={`${cardClass} mb-6 p-4`}>
        <div className="flex items-center gap-4">
          <label className="block text-sm font-medium text-gray-700">
            Tipo de Solicitação:
          </label>
          <select
            value={tipoSolicitacao}
            onChange={(e) => setTipoSolicitacao(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="REABERTURA">Reabertura</option>
            <option value="ABERTURA">Abertura</option>
          </select>
          <label className="block text-sm font-medium text-gray-700">
            Filtrar por Status:
          </label>
          <select
            value={filtroStatus}
            onChange={(e) => setFiltroStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="PENDENTE">Pendentes</option>
            <option value="APROVADO">Aprovadas</option>
            <option value="NEGADO">Negadas</option>
          </select>
        </div>
      </div>

      <div className={cardClass}>
        <div className={cardHeaderClass}>
          <h3 className={cardHeaderTitleClass}>
            Solicitações de{" "}
            {tipoSolicitacao === "REABERTURA" ? "Reabertura" : "Abertura"}{" "}
            {getStatusLabel(filtroStatus)}s
          </h3>
          <span className="text-sm text-gray-500">
            {totalRegistros} registro(s) encontrado(s)
          </span>
        </div>

        {loading && solicitacoes.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            Carregando solicitações...
          </div>
        ) : solicitacoes.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            Nenhuma solicitação encontrada para o status selecionado
          </div>
        ) : (
          <>
            <div className={tableWrapperClass}>
              <table className={tableClass}>
                <thead className={tableHeadClass}>
                  <tr>
                    <th className={tableHeaderCellClass}>
                      Solicitante
                    </th>
                    {tipoSolicitacao === "REABERTURA" ? (
                      <>
                        <th className={tableHeaderCellClass}>
                          Data do Caixa
                        </th>
                        <th className={tableHeaderCellClass}>
                          Valor do Caixa
                        </th>
                      </>
                    ) : (
                      <th className={tableHeaderCellClass}>
                        Datas sem Lançamento
                      </th>
                    )}
                    <th className={tableHeaderCellClass}>
                      Data Solicitação
                    </th>
                    <th className={tableHeaderCellClass}>
                      Status
                    </th>
                    <th className={tableHeaderCellClass}>
                      Motivo
                    </th>
                    <th className={tableHeaderCellRightClass}>
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className={tableBodyClass}>
                  {solicitacoesPaginadas.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <User className="h-4 w-4 text-gray-400 mr-2" />
                          <div className="text-sm font-medium text-gray-900">
                            {item.solicitante?.nome || "N/A"}
                          </div>
                        </div>
                      </td>
                      {tipoSolicitacao === "REABERTURA" ? (
                        <>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                              <div className="text-sm font-medium text-gray-900">
                                {formatarData(item.caixa?.data)}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <DollarSign className="h-4 w-4 text-green-600 mr-1" />
                              <span className="text-sm font-medium text-gray-900">
                                {formatCurrency(item.caixa?.valorTotal)}
                              </span>
                            </div>
                          </td>
                        </>
                      ) : (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 text-yellow-600 mr-2" />
                            <span className="text-sm font-medium text-gray-900">
                              {item.datasCaixaSemLancamento?.length || 0}{" "}
                              data(s)
                            </span>
                            <button
                              onClick={() => abrirModalDatas(item)}
                              className="ml-2 text-blue-600 hover:text-blue-900 transition-colors"
                              title="Ver todas as datas"
                            >
                              <Eye size={14} />
                            </button>
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatarData(item.dataSolicitacao)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                            getCurrentStatus(item)
                          )}`}
                        >
                          {getStatusIcon(getCurrentStatus(item))}
                          <span className="ml-1">
                            {getStatusLabel(getCurrentStatus(item))}
                          </span>
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs">
                          <div className="truncate" title={item.motivo}>
                            {item.motivo}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => abrirModalAcao(item)}
                          disabled={loading}
                          className="text-blue-600 hover:text-blue-900 disabled:text-gray-400 p-1 rounded transition-colors"
                          title="Ver Detalhes e Responder"
                        >
                          <MessageSquare size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Controles de Paginação */}
            {totalPaginas > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Mostrando {indiceInicial + 1} até{" "}
                    {Math.min(indiceFinal, totalRegistros)} de {totalRegistros}{" "}
                    registros
                  </div>

                  <div className="flex items-center space-x-2">
                    {/* Primeira página */}
                    <button
                      onClick={irParaPrimeiraPagina}
                      disabled={paginacao.paginaAtual === 1}
                      className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Primeira página"
                    >
                      <ChevronsLeft size={16} />
                    </button>

                    {/* Página anterior */}
                    <button
                      onClick={irParaPaginaAnterior}
                      disabled={paginacao.paginaAtual === 1}
                      className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Página anterior"
                    >
                      <ChevronLeft size={16} />
                    </button>

                    {/* Números das páginas */}
                    <div className="flex space-x-1">
                      {gerarNumerosPaginas().map((numeroPagina) => (
                        <button
                          key={numeroPagina}
                          onClick={() => irParaPagina(numeroPagina)}
                          className={`px-3 py-2 rounded-md text-sm font-medium ${
                            paginacao.paginaAtual === numeroPagina
                              ? "bg-blue-600 text-white"
                              : "bg-white text-gray-500 border border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          {numeroPagina}
                        </button>
                      ))}
                    </div>

                    {/* Próxima página */}
                    <button
                      onClick={irParaProximaPagina}
                      disabled={paginacao.paginaAtual === totalPaginas}
                      className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Próxima página"
                    >
                      <ChevronRight size={16} />
                    </button>

                    {/* Última página */}
                    <button
                      onClick={irParaUltimaPagina}
                      disabled={paginacao.paginaAtual === totalPaginas}
                      className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Última página"
                    >
                      <ChevronsRight size={16} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* MODAL PARA VER DATAS */}
      {showDatasModal && selectedSolicitacao && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh]">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">
                Datas sem Lançamento
              </h3>

              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="mt-2 space-y-1 max-h-48">
                  {selectedSolicitacao.datasCaixaSemLancamento?.map(
                    (data, index) => (
                      <span
                        key={index}
                        className="inline-block text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded mr-1 mb-1"
                      >
                        {formatarData(data)}
                      </span>
                    )
                  )}
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={fecharModalDatas}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL PARA AÇÕES */}
      {showAcaoModal && selectedSolicitacao && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh]">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">
                Detalhes da Solicitação de{" "}
                {tipoSolicitacao === "REABERTURA" ? "Reabertura" : "Abertura"}
              </h3>

              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">
                      Solicitante:
                    </span>
                    <div className="mt-1">
                      {selectedSolicitacao.solicitante?.nome}
                    </div>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">
                      Data da Solicitação:
                    </span>
                    <div className="mt-1">
                      {formatarData(selectedSolicitacao.dataSolicitacao)}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <span className="font-medium text-gray-700">
                      Motivo da Solicitação:
                    </span>
                    <div className="mt-1 p-2 bg-white border rounded">
                      {selectedSolicitacao.motivo}
                    </div>
                  </div>
                </div>
              </div>

              {/* ADICIONE ESTA PARTE AQUI - BOTÕES PARA APROVAR/NEGAR */}
              {selectedSolicitacao.status?.toUpperCase() === "PENDENTE" && (
                <div className="mb-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <h4 className="font-medium text-yellow-800 mb-2">
                    Responder Solicitação
                  </h4>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Motivo da Resposta (opcional):
                    </label>
                    <textarea
                      value={motivoResposta}
                      onChange={(e) => setMotivoResposta(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                      rows={3}
                      placeholder="Digite um motivo para sua decisão..."
                    />
                  </div>
                  <div className="flex gap-2">
                    <p>{selectedSolicitacao.status.toString()}</p>
                    <button
                      onClick={() => responderSolicitacao("APROVADO")}
                      disabled={loading}
                      className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                    >
                      <Check size={16} />
                      {loading ? "Processando..." : "Aprovar"}
                    </button>
                    <button
                      onClick={() => responderSolicitacao("NEGADO")}
                      disabled={loading}
                      className="bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                    >
                      <X size={16} />
                      {loading ? "Processando..." : "Negar"}
                    </button>
                  </div>
                </div>
              )}

              <div className="flex gap-2 justify-end">
                <button
                  onClick={fecharModalAcao}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  );
};

export default AtualizaSolicitacaoCaixa;
